import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import DataTable from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './ContentManagement.css';

const { Skeleton } = SkeletonLoaderComponents;

const CONTENT_LIBRARIES = [
  { id: 'chinese-characters', name: 'Chinese Characters', prefix: 'chinese-characters/' },
  { id: 'math-concepts', name: 'Math Concepts', prefix: 'math-concepts/' }
];

const VALID_FILE_TYPES = {
  'chinese-characters': {
    extensions: ['.json', '.png', '.jpg', '.jpeg', '.svg'],
    mimeTypes: ['application/json', 'image/png', 'image/jpeg', 'image/svg+xml']
  },
  'math-concepts': {
    extensions: ['.json', '.png', '.jpg', '.jpeg', '.svg', '.pdf'],
    mimeTypes: ['application/json', 'image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf']
  }
};

const ContentManagement = () => {
  const { user } = useAdminAuth();
  const [selectedLibrary, setSelectedLibrary] = useState(CONTENT_LIBRARIES[0]);
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch content list
  useEffect(() => {
    fetchContentList();
  }, [selectedLibrary, refreshTrigger]);

  const fetchContentList = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_ENDPOINTS.ADMIN.CONTENT_MANAGEMENT}/list?prefix=${encodeURIComponent(selectedLibrary.prefix)}`,
        {
          headers: {
            'Authorization': `Bearer ${await getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.statusText}`);
      }

      const data = await response.json();
      setContentItems(data.items || []);
    } catch (err) {
      console.error('[ContentManagement] Error fetching content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = async () => {
    const session = await fetchAuthSession();
    // CRITICAL: Use idToken (not accessToken) for admin authorization
    // cognito:groups claim is ONLY in ID token, NOT in access token
    return session.tokens?.idToken?.toString() || '';
  };

  const handleLibraryChange = (library) => {
    setSelectedLibrary(library);
    setContentItems([]);
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
    setUploadFile(null);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const validTypes = VALID_FILE_TYPES[selectedLibrary.id];

    if (!validTypes.extensions.includes(fileExtension)) {
      setUploadError(
        `Invalid file type. Allowed types for ${selectedLibrary.name}: ${validTypes.extensions.join(', ')}`
      );
      setUploadFile(null);
      return;
    }

    setUploadFile(file);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploadProgress(0);
    setUploadError(null);

    try {
      // Step 1: Get presigned upload URL
      const urlResponse = await fetch(`${API_ENDPOINTS.ADMIN.CONTENT_MANAGEMENT}/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          fileName: uploadFile.name,
          contentType: uploadFile.type,
          prefix: selectedLibrary.prefix
        })
      });

      if (!urlResponse.ok) {
        const errorData = await urlResponse.json();
        throw new Error(errorData.error || 'Failed to get upload URL');
      }

      const { uploadUrl, key } = await urlResponse.json();

      // Step 2: Upload file to S3
      setUploadProgress(50);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': uploadFile.type
        },
        body: uploadFile
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setUploadProgress(100);

      // Step 3: Log audit event
      await logAuditEvent('CONTENT_UPLOADED', {
        key,
        fileName: uploadFile.name,
        library: selectedLibrary.id,
        size: uploadFile.size
      });

      // Success - close modal and refresh list
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadFile(null);
        setRefreshTrigger(prev => prev + 1);
      }, 500);

    } catch (err) {
      console.error('[ContentManagement] Upload error:', err);
      setUploadError(err.message);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.ADMIN.CONTENT_MANAGEMENT}/download?key=${encodeURIComponent(item.key)}`,
        {
          headers: {
            'Authorization': `Bearer ${await getAuthToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }

      const { downloadUrl } = await response.json();
      
      // Open download URL in new tab
      window.open(downloadUrl, '_blank');

      // Log audit event
      await logAuditEvent('CONTENT_DOWNLOADED', {
        key: item.key,
        fileName: item.name,
        library: selectedLibrary.id
      });

    } catch (err) {
      console.error('[ContentManagement] Download error:', err);
      alert(`Failed to download file: ${err.message}`);
    }
  };

  const logAuditEvent = async (action, details) => {
    try {
      await fetch(`${API_ENDPOINTS.ADMIN.AUDIT_LOGGER}/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          action,
          targetType: 'content',
          targetId: details.key,
          details,
          adminId: user.userId,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('[ContentManagement] Failed to log audit event:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const columns = [
    {
      key: 'name',
      label: 'File Name',
      sortable: true,
      render: (item) => (
        <div className="content-management__file-name">
          <span className="content-management__file-icon">
            {item.type.includes('image') ? '🖼️' : item.type.includes('json') ? '📄' : '📎'}
          </span>
          {item.name}
        </div>
      )
    },
    {
      key: 'size',
      label: 'Size',
      sortable: true,
      render: (item) => formatFileSize(item.size)
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true
    },
    {
      key: 'lastModified',
      label: 'Last Modified',
      sortable: true,
      render: (item) => formatDate(item.lastModified)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <button
          onClick={() => handleDownload(item)}
          className="content-management__action-button"
        >
          Download
        </button>
      )
    }
  ];

  return (
    <ErrorBoundary>
      <div className="content-management">
        <div className="content-management__header">
          <h1 className="content-management__title">Content Management</h1>
          <p className="content-management__description">
            Manage learning content libraries for Chinese characters and math concepts
          </p>
        </div>

        {/* Library selector */}
        <div className="content-management__library-selector">
          {CONTENT_LIBRARIES.map(library => (
            <button
              key={library.id}
              onClick={() => handleLibraryChange(library)}
              className={`content-management__library-button ${
                selectedLibrary.id === library.id ? 'content-management__library-button--active' : ''
              }`}
            >
              {library.name}
            </button>
          ))}
        </div>

        {/* Actions bar */}
        <div className="content-management__actions">
          <button
            onClick={handleUploadClick}
            className="content-management__upload-button"
          >
            📤 Upload Content
          </button>
          <button
            onClick={fetchContentList}
            className="content-management__refresh-button"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Content list */}
        <div className="content-management__content">
          {error && (
            <div className="content-management__error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} width="100%" height="60px" className="admin-skeleton-gap" />
              ))}
            </>
          ) : contentItems.length > 0 ? (
            <DataTable
              data={contentItems}
              columns={columns}
              sortable={true}
              pagination={true}
              pageSize={20}
            />
          ) : (
            <div className="content-management__empty">
              <p>No content found in {selectedLibrary.name}</p>
            </div>
          )}
        </div>

        {/* Upload modal */}
        {showUploadModal && (
          <Modal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            title={`Upload to ${selectedLibrary.name}`}
          >
            <div className="content-management__upload-modal">
              <div className="content-management__upload-info">
                <p>
                  <strong>Allowed file types:</strong>{' '}
                  {VALID_FILE_TYPES[selectedLibrary.id].extensions.join(', ')}
                </p>
              </div>

              <div className="content-management__file-input">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept={VALID_FILE_TYPES[selectedLibrary.id].extensions.join(',')}
                  id="content-file-input"
                />
                <label htmlFor="content-file-input" className="content-management__file-label">
                  {uploadFile ? uploadFile.name : 'Choose file...'}
                </label>
              </div>

              {uploadError && (
                <div className="content-management__upload-error">
                  {uploadError}
                </div>
              )}

              {uploadProgress > 0 && (
                <div className="content-management__progress">
                  <div className="content-management__progress-bar">
                    <div
                      className="content-management__progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="content-management__progress-text">
                    {uploadProgress}%
                  </span>
                </div>
              )}

              <div className="content-management__modal-actions">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="content-management__cancel-button"
                  disabled={uploadProgress > 0 && uploadProgress < 100}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className="content-management__submit-button"
                  disabled={!uploadFile || uploadProgress > 0}
                >
                  Upload
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ContentManagement;
